import { useMemo, useRef } from 'react'

import { useCurrentTime } from 'shared/contexts'

import {
  getMeetingStatus,
  getUpNextMeetingId,
  DISPLAY_STATUS,
  type DisplayStatus,
} from '../utils/meeting-helpers'

const { UPCOMING, UP_NEXT, IN_PROGRESS, CANCELLED, STARTING_SOON, NO_SHOW, RESCHEDULED } = DISPLAY_STATUS

export interface UseMeetingStatusReturn {
  status: DisplayStatus
  startTime: string | undefined
  isInProgress: boolean
  isCancelled: boolean
  isStartingSoon: boolean
  isUpcoming: boolean
  isUpNext: boolean
  isNoShow: boolean
  isRescheduled: boolean
}

/**
 * Batch hook for calculating statuses of all meetings in the list
 * Accepts meetings array from parent (already loaded from RTK Query)
 * Uses shared CurrentTimeContext to keep all status calculations in sync
 *
 * @param meetings - Array of meetings from RTK Query
 * @returns Map of meeting UUID to status info (status, displayText, boolean flags)
 */
export const useBatchMeetingStatuses = (meetings: any[] = []): Map<string, UseMeetingStatusReturn> => {
  // Use shared current time to keep all status displays in sync
  const currentTime = useCurrentTime()
  const prevMapRef = useRef<Map<string, UseMeetingStatusReturn>>(new Map())

  const statusesMap = useMemo(() => {
    const newMap = new Map<string, UseMeetingStatusReturn>()

    if (!meetings || !Array.isArray(meetings)) {
      if (prevMapRef.current.size === 0) {
        return prevMapRef.current
      }
      prevMapRef.current = newMap
      return newMap
    }

    let hasChanges = false
    const upNextMeetingId = getUpNextMeetingId(meetings, currentTime)

    meetings.forEach((meeting) => {
      if (meeting && meeting.uuid) {
        let status = getMeetingStatus(meeting, currentTime)

        if (meeting.uuid === upNextMeetingId && status === UPCOMING) {
          status = UP_NEXT
        }

        const startTime = meeting.startTime

        const statusData: UseMeetingStatusReturn = {
          status,
          startTime,
          isInProgress: status === IN_PROGRESS,
          isCancelled: status === CANCELLED,
          isStartingSoon: status === STARTING_SOON,
          isUpcoming: status === UPCOMING,
          isUpNext: status === UP_NEXT,
          isNoShow: status === NO_SHOW,
          isRescheduled: status === RESCHEDULED,
        }

        newMap.set(meeting.uuid, statusData)

        const prevStatus = prevMapRef.current.get(meeting.uuid)
        if (!prevStatus
            || prevStatus.status !== status
            || prevStatus.startTime !== startTime) {
          hasChanges = true
        }
      }
    })

    // Also check if meetings were added/removed
    if (prevMapRef.current.size !== newMap.size) {
      hasChanges = true
    }

    // If nothing changed, return the previous Map to keep reference stable
    if (!hasChanges) {
      return prevMapRef.current
    }

    prevMapRef.current = newMap
    return newMap
  }, [meetings, currentTime])

  return statusesMap
}
